import { ReactRenderer } from "@tiptap/react";
import MentionBase from "@tiptap/extension-mention";
import { Extension } from "@tiptap/core";
import { TextStyle } from "@tiptap/extension-text-style";
import MentionList from "./MentionList.jsx";

/**
 * Parse a stored value back into a form that tiptap's setContent can accept.
 */
export function parseStoredValue(val) {
  if (!val) return "";
  if (typeof val === "string" && val.trimStart().startsWith("{")) {
    try {
      return JSON.parse(val);
    } catch {
      // Not valid JSON — fall through and treat as markdown
    }
  }
  return val;
}

/**
 * Clamp a fixed-position popover so it stays inside the viewport.
 *
 * @param {DOMRect} anchorRect - bounding rect of the anchor (caret/chip) the popover points at
 * @param {HTMLElement} element - the popover element (must be in the DOM so it can be measured)
 * @param {{ gap?: number, margin?: number }} [opts] - gap below the anchor, min distance from edges
 * @returns {{ top: number, left: number }} fixed-position coordinates
 */
export function getViewportPosition(anchorRect, element, { gap = 4, margin = 8 } = {}) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = element.offsetWidth;
  const height = element.offsetHeight;

  // Horizontal: prefer the anchor's left edge, but pull back from the right border.
  let left = anchorRect.left;
  if (left + width > vw - margin) left = vw - width - margin;
  if (left < margin) left = margin;

  // Vertical: default below the anchor; flip above when it would overflow the bottom.
  let top = anchorRect.bottom + gap;
  if (top + height > vh - margin) {
    const above = anchorRect.top - height - gap;
    top = above >= margin ? above : Math.max(margin, vh - height - margin);
  }

  return { top, left };
}

/**
 * Build the Mention extension with user mentions and command support
 */
export function buildMentionExt(mentionsRef, commandsRef) {
  const MentionMd = MentionBase.extend({
    addAttributes() {
      const parentAttributes = this.parent?.() || {};
      return {
        ...parentAttributes,
        commandKey: { default: null },
        dataCommand: { default: null },
        customValue: { default: null },
        "data-command": {
          default: null,
          renderHTML: (attributes) => {
            return {
              "data-command": attributes["data-command"],
            };
          },
        },
      };
    },
    renderHTML({ node, HTMLAttributes }) {
      let commandKey = node.attrs.commandKey || node.attrs["data-command"];
      let label = node.attrs.label;
      let mentionSuggestionChar = node.attrs.mentionSuggestionChar;

      let text = mentionSuggestionChar + label || "";

      let typeClass = "mention-chip";
      if (commandKey) {
        typeClass = "mention-chip command-chip command-" + commandKey;
        let commandType = commandsRef.current?.[commandKey];
        text = "/" + (commandType?.label || commandKey) + ": " + label;
      }
      return [
        "span",
        {
          ...HTMLAttributes,
          class: typeClass,
          "data-type": "mention",
          "data-command": commandKey,
        },
        text,
      ];
    },
    addStorage() {
      return {
        markdown: {
          serialize(state, node) {
            let attrs = node.attrs;
            let commandKey = attrs.commandKey || attrs["data-command"];
            let isUser = !attrs["data-command"];
            let mentionSuggestionChar = attrs.mentionSuggestionChar;

            if (isUser) {
              commandKey = "user";
              mentionSuggestionChar = "@";
            } else {
              mentionSuggestionChar = "/";
            }

            state.write(
              `${mentionSuggestionChar}[${attrs.label}](${commandKey}:${attrs.id})`,
            );
          },
          parse: {
            setup(markdownit) {
              markdownit.inline.ruler.before(
                "text",
                "slash_text",
                (state, silent) => {
                  let pos = state.pos;
                  while (pos < state.posMax) {
                    const ch = state.src.charCodeAt(pos);
                    if (
                      ch === 0x0a ||
                      ch === 0x21 ||
                      ch === 0x23 ||
                      ch === 0x24 ||
                      ch === 0x25 ||
                      ch === 0x26 ||
                      ch === 0x2a ||
                      ch === 0x2b ||
                      ch === 0x2d ||
                      ch === 0x3a ||
                      ch === 0x3c ||
                      ch === 0x3d ||
                      ch === 0x3e ||
                      ch === 0x40 ||
                      ch === 0x5b ||
                      ch === 0x5c ||
                      ch === 0x5d ||
                      ch === 0x5e ||
                      ch === 0x5f ||
                      ch === 0x60 ||
                      ch === 0x7b ||
                      ch === 0x7d ||
                      ch === 0x7e ||
                      ch === 0x2f
                    ) {
                      break;
                    }
                    pos += 1;
                  }
                  if (pos === state.pos) return false;
                  if (!silent) {
                    state.pending += state.src.slice(state.pos, pos);
                  }
                  state.pos = pos;
                  return true;
                },
              );
              markdownit.inline.ruler.before(
                "text",
                "mention",
                (mState, silent) => {
                  const trigger = mState.src[mState.pos];
                  if (trigger !== "@" && trigger !== "/") return false;
                  if (
                    mState.src.charCodeAt(mState.pos + 1) !== "[".charCodeAt(0)
                  )
                    return false;
                  const closeIdx = mState.src.indexOf("](", mState.pos + 2);
                  if (closeIdx === -1) return false;
                  const closeParenIdx = mState.src.indexOf(")", closeIdx + 2);
                  if (closeParenIdx === -1) return false;
                  if (!silent) {
                    const label = mState.src.slice(mState.pos + 2, closeIdx);
                    const info = mState.src.slice(closeIdx + 2, closeParenIdx);
                    const token = mState.push("html_inline", "", 0);
                    const [type, id] = info.split(":");

                    if (type === "user") {
                      token.content = `<span class="mention-chip" data-type="mention" data-mention-type="user" data-id="${id}" data-label="${label}">@${label}</span>`;
                    } else {
                      let commandType = commandsRef.current?.[type];
                      token.content = `<span class="mention-chip command-chip command-${type}" data-type="mention" data-mention-type="command" data-command="${type}" data-id="${id}" data-label="${label}">/${commandType?.label}: ${label}</span>`;
                    }
                  }
                  mState.pos = closeParenIdx + 1;
                  return true;
                },
              );
            },
          },
        },
      };
    },
  });

  const mentions = {
    chars: "@",
    items: ({ query }) =>
      (mentionsRef.current || [])
        .filter((u) => u.label.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 8),

    render: () => {
      let component;
      let scrollCleanup = null;

      const applyPosition = (element, getRectFn) => {
        const rect = typeof getRectFn === "function" ? getRectFn() : null;
        if (rect) {
          const { top, left } = getViewportPosition(rect, element);
          element.style.top = `${top}px`;
          element.style.left = `${left}px`;
        }
      };

      return {
        onStart(props) {
          component = new ReactRenderer(MentionList, {
            props,
            editor: props.editor,
          });
          if (!props.clientRect) return;
          const rect = props.clientRect();
          if (rect) {
            Object.assign(component.element.style, {
              position: "fixed",
              top: `${rect.bottom + 4}px`,
              left: `${rect.left}px`,
              zIndex: 9999,
            });
            document.body.appendChild(component.element);
            // Clamp now that the element is in the DOM and can be measured.
            applyPosition(component.element, props.clientRect);
            const onScroll = () =>
              applyPosition(component.element, props.clientRect);
            window.addEventListener("scroll", onScroll, true);
            scrollCleanup = () =>
              window.removeEventListener("scroll", onScroll, true);
          }
        },
        onUpdate(props) {
          component.updateProps(props);
          if (!props.clientRect) return;
          applyPosition(component.element, props.clientRect);
        },
        onKeyDown(props) {
          if (props.event.key === "Escape") {
            if (scrollCleanup) {
              scrollCleanup();
              scrollCleanup = null;
            }
            component.element.remove();
            component.destroy();
            return true;
          }
          return component.ref?.onKeyDown(props);
        },
        onExit() {
          if (scrollCleanup) {
            scrollCleanup();
            scrollCleanup = null;
          }
          component.element.remove();
          component.destroy();
        },
      };
    },
  };

  const commands = {
    char: "/",
    allowSpaces: true,
    items: ({ query }) => {
      let commandKey = query[0];
      let subQuery = query.substring(1);
      const commands = commandsRef.current;
      if (commands?.[commandKey]) {
        // Print sublist of options
        return (
          commands[commandKey].options
            .filter((option) => {
              return option.label
                .toLowerCase()
                .includes(subQuery.toLowerCase());
            })
            .map((option) => ({
              ...option,
              commandKey,
            })) || []
        );
      } else {
        // Print command list
        return Object.keys(commands || {})
          .map((key) => ({
            ...commands[key],
            key,
          }))
          .filter((command) =>
            command.key.toLowerCase().includes(query.toLowerCase()),
          );
      }
    },
    render: () => {
      let component;
      let scrollCleanup = null;

      const applyPosition = (element, getRectFn) => {
        const rect = typeof getRectFn === "function" ? getRectFn() : null;
        if (rect) {
          const { top, left } = getViewportPosition(rect, element);
          element.style.top = `${top}px`;
          element.style.left = `${left}px`;
        }
      };

      return {
        onStart(props) {
          if (!commandsRef.current) return;
          component = new ReactRenderer(MentionList, {
            props,
            editor: props.editor,
          });
          if (!props.clientRect) return;
          const rect = props.clientRect();
          if (rect) {
            Object.assign(component.element.style, {
              position: "fixed",
              top: `${rect.bottom + 4}px`,
              left: `${rect.left}px`,
              zIndex: 9999,
            });
            document.body.appendChild(component.element);
            // Clamp now that the element is in the DOM and can be measured.
            applyPosition(component.element, props.clientRect);
            const onScroll = () =>
              applyPosition(component.element, props.clientRect);
            window.addEventListener("scroll", onScroll, true);
            scrollCleanup = () =>
              window.removeEventListener("scroll", onScroll, true);
          }
        },
        onUpdate(props) {
          const commands = commandsRef.current;
          if (!commands) return;

          let commandKey = props.query[0];
          let label = "No commands found";
          if (commands[commandKey]) {
            label = "No " + commands[commandKey].label + " found";
          }

          component.updateProps({
            ...props,
            label,
          });
          if (!props.clientRect) return;
          applyPosition(component.element, props.clientRect);
        },
        onKeyDown(props) {
          if (props.event.key === "Escape") {
            if (scrollCleanup) {
              scrollCleanup();
              scrollCleanup = null;
            } else {
              component.element.remove();
              component.destroy();
            }
            return true;
          }
          return component?.ref?.onKeyDown(props);
        },
        onExit() {
          if (scrollCleanup) {
            scrollCleanup();
            scrollCleanup = null;
          }
          if (component) {
            component.element.remove();
            component.destroy();
          }
        },
      };
    },
  };

  let suggestions = [];

  if (mentionsRef.current) suggestions.push(mentions);
  if (commandsRef.current) suggestions.push(commands);

  return MentionMd.configure({
    HTMLAttributes: { class: "mention-chip" },
    suggestions: suggestions,
  });
}

// Node types that already own Tab and must keep it.
const TAB_IS_TAKEN = ["listItem", "taskItem", "tableCell", "tableHeader"];

/**
 * Tab types a tab instead of walking focus out of the editor.
 *
 * The browser's default for Tab in a contenteditable is "focus the next control", which is right
 * for a one-line input and wrong here: the editor is the LAST field of every form that has one
 * (title on top, description below, buttons after), so leaving costs the writer their place in the
 * text and gains them nothing. Typing an indent is what a text area is expected to do.
 *
 * Lists and tables keep their own meaning for Tab — indent the bullet, move to the next cell. This
 * extension runs first (`priority`) and bows out by returning false when the cursor is inside one,
 * which hands the keystroke back to the list/table bindings. The check walks the ancestors of the
 * selection rather than asking `editor.isActive(name)`, which throws for a node type the schema
 * does not have (a caller can switch lists or tables off via `tools`).
 *
 * Shift+Tab is deliberately left unbound, so it still walks focus BACKWARDS out of the editor and
 * on to the field above. With Tab captured that is the only way out by keyboard, so binding it too
 * would trap the user in the text.
 */
export const TabInsertsIndent = Extension.create({
  name: "tabInsertsIndent",
  priority: 1000,
  addKeyboardShortcuts() {
    return {
      Tab: () => {
        const { $from } = this.editor.state.selection;
        for (let depth = $from.depth; depth > 0; depth--) {
          if (TAB_IS_TAKEN.includes($from.node(depth).type.name)) return false;
        }
        // An explicit text node, not the string form: `insertContent("\t")` goes through the HTML
        // parser, which discards a lone whitespace character.
        return this.editor.commands.insertContent({ type: "text", text: "\t" });
      },
    };
  },
});

/**
 * Extends TextStyle with an explicit markdown serializer that writes inline HTML spans.
 */
export const MarkdownTextStyle = TextStyle.extend({
  addStorage() {
    return {
      markdown: {
        serialize: {
          open(_state, mark) {
            const parts = [];
            if (mark.attrs.color) parts.push(`color: ${mark.attrs.color}`);
            if (mark.attrs.backgroundColor)
              parts.push(`background-color: ${mark.attrs.backgroundColor}`);
            return parts.length ? `<span style="${parts.join("; ")}">` : "";
          },
          close(_state, mark) {
            return mark.attrs.color || mark.attrs.backgroundColor
              ? "</span>"
              : "";
          },
          mixable: true,
          expelEnclosingWhitespace: true,
        },
        parse: {}, // HTML spans are parsed back via Tiptap's HTML parser (html: true)
      },
    };
  },
});

/**
 * Give every column of the table at the cursor an explicit width, so the TABLE follows its columns.
 *
 * This is what makes a table resizable as a whole. prosemirror-tables' `updateColumnsOnResize` sets
 * `table.style.width = <sum of column widths>px` only when EVERY column has a `colwidth`; if even one
 * is null it clears the width and sets `min-width` instead, letting the stylesheet's `width: 100%`
 * win. TipTap's `insertTable` creates every cell with `colwidth: null`, so a fresh table sat in that
 * second state permanently: dragging a divider redistributed space between columns and the table
 * itself stayed stubbornly full width — including the last column's handle, which is exactly where
 * you'd expect to grab a table's right edge.
 *
 * Widths are read from what is already on screen (the rendered `<col>` elements, else the header
 * cells), so seeding is visually a no-op — the table looks identical, it just becomes draggable.
 * Falls back to an equal split of the editor's content width when nothing can be measured (e.g. a
 * table seeded before it has ever been laid out).
 *
 * Colspans: a cell spanning N columns owns N entries in its own `colwidth` array, which is why the
 * per-cell write walks the column cursor rather than assuming one column per cell.
 */
export function seedTableColumnWidths(editor, docPos = null) {
  if (!editor || editor.isDestroyed) return false;

  const { state } = editor;
  // `docPos` targets a table the CURSOR is not in — the resize plugin knows which cell's edge the
  // pointer is on, which is the table to seed when someone reaches for a handle without clicking first.
  let $from;
  try {
    $from = docPos === null ? state.selection.$from : state.doc.resolve(docPos);
  } catch {
    return false;
  }

  // Walk up to the table node wrapping that position.
  let tablePos = null;
  let tableNode = null;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === "table") {
      tableNode = node;
      tablePos = $from.before(depth);
      break;
    }
  }
  if (!tableNode) return false;

  const firstRow = tableNode.firstChild;
  if (!firstRow) return false;

  // How many columns the table has, counting colspans.
  let columnCount = 0;
  firstRow.forEach((cell) => {
    columnCount += cell.attrs.colspan || 1;
  });
  if (columnCount === 0) return false;

  // Measured widths, in column order. The rendered <col> elements are what prosemirror itself keeps
  // in sync, so they are the most faithful source; header cells are the fallback.
  // nodeDOM gives the nodeView's root, which for a table is prosemirror's `.tableWrapper` div.
  const dom = editor.view.nodeDOM(tablePos);
  const rootEl = dom && dom.nodeType === 1 ? dom : null;
  const tableEl = rootEl?.tagName === "TABLE" ? rootEl : (rootEl?.querySelector("table") ?? null);
  const measured = [];
  if (tableEl) {
    const cols = tableEl.querySelectorAll("colgroup col");
    if (cols.length === columnCount) {
      for (const col of cols) measured.push(Math.round(parseFloat(col.style.width) || col.getBoundingClientRect().width));
    } else {
      const cells = tableEl.querySelectorAll("tr:first-child > th, tr:first-child > td");
      for (const cell of cells) {
        const w = Math.round(cell.getBoundingClientRect().width);
        const span = Number(cell.getAttribute("colspan")) || 1;
        for (let i = 0; i < span; i++) measured.push(Math.round(w / span));
      }
    }
  }

  const fallbackTotal = editor.view.dom.clientWidth || 640;
  const evenWidth = Math.max(40, Math.floor(fallbackTotal / columnCount));
  const widthFor = (index) => {
    const w = measured[index];
    return Number.isFinite(w) && w > 0 ? w : evenWidth;
  };

  const tr = state.tr;
  let changed = false;
  tableNode.forEach((row, rowOffset) => {
    let column = 0;
    row.forEach((cell, cellOffset) => {
      const span = cell.attrs.colspan || 1;
      const next = [];
      for (let i = 0; i < span; i++) next.push(widthFor(column + i));
      const current = cell.attrs.colwidth;
      const same = Array.isArray(current) && current.length === next.length && current.every((v, i) => v === next[i]);
      if (!same) {
        // +1 for the table's own opening token, +1 for the row's.
        const cellPos = tablePos + 1 + rowOffset + 1 + cellOffset;
        tr.setNodeAttribute(cellPos, "colwidth", next);
        changed = true;
      }
      column += span;
    });
  });

  if (!changed) return false;
  editor.view.dispatch(tr);
  return true;
}
